import { ChatField, UUID } from '@indigohive/cogfy-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { Button, Input, Mentions, MentionsValue, ToolTip } from '../../../../../components'
import { CollectionState, useFields, useSelectedFieldToUpdate } from '../../../../../lib'
import { CollectionPageController } from '../../../../../pages/CollectionPageV2/collection-page-controller'
import { ChatConfigurationsValues } from '..'
import { useGenerateChatAppId } from '../../../../../hooks/use-generate-chat-app-id'
import { ClipboardIcon, RefreshCcw } from 'lucide-react'

const defaultInitialMessage = 'Hello! How may I help you?'

export type ChatConfigurationsBaseProps = {
  controller: CollectionPageController
  state: CollectionState
  values: ChatConfigurationsValues
  onChange: (prop: keyof ChatConfigurationsValues, value: any) => void
}

export function ChatConfigurationsBase (props: ChatConfigurationsBaseProps) {
  const { controller, state, values, onChange } = props

  const { t } = useTranslation()
  const selectedField = useSelectedFieldToUpdate(state) as ChatField
  const fields = useFields(state)
  const [recentlyCopiedAppId, setRecentlyCopiedAppId] = useState(false)

  const handleCopyAppId = () => {
    setRecentlyCopiedAppId(true)

    navigator.clipboard.writeText(selectedField.data?.chat?.appId || '')

    setTimeout(() => {
      setRecentlyCopiedAppId(false)
    }, 2000)
  }

  const { mutateAsync: generateChatAppId } = useGenerateChatAppId(selectedField.id)

  const onGenerateChatAppId = async () => {
    const { appId } = await generateChatAppId()

    controller.onUpdateFieldData(
      selectedField.id,
      {
        chat: {
          ...selectedField?.data?.chat,
          appId
        }
      }
    )
  }

  const onChangeInitialMessage = useDebouncedCallback((newValue: MentionsValue) => {
    controller.onUpdateFieldData(
      selectedField.id,
      {
        chat: {
          ...selectedField?.data?.chat,
          initialMessage: {
            type: 'template',
            template: {
              strings: newValue.parts,
              args: newValue.options.map(option => ({ fieldId: option.id as UUID }))
            }
          }
        }
      }
    )
  }, 300)

  const mentionOptions = useMemo(() => {
    return fields!
      .filter(field => field.type === 'text' || field.type === 'json' || field.type === 'number')
      .filter(field => field.id !== selectedField?.id)
      .map(field => ({ id: field.id, name: field.name }))
  }, fields!)

  const initialMessage = values.initialMessage
  const origins = values.origins

  return (
    <label className="fieldset w-full">
      <div className='flex items-center'>
        <span className="text-sm py-1">{t('editFieldDrawer:Set custom initial message')}</span>
        <ToolTip
          description={t('ToolTipDescription:Initial message shown in chat')}
          type="info"
        />
      </div>
      <Mentions
        placeholder={defaultInitialMessage}
        options={mentionOptions}
        value={initialMessage}
        onChange={value => {
          onChange('initialMessage', value)
          onChangeInitialMessage(value)
        }}
      />
      <div className="flex items-center">
        <div className="flex flex-row items-center grow">
          <span className="text-sm py-1">
            App Id
          </span>
          <ToolTip
            description={t('editFieldDrawer:App Id for Cogfy FAB')}
            type="info"
          />
        </div>
        <Button
          size="xs"
          color='primary'
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onClick={() => onGenerateChatAppId()}
        >
          <RefreshCcw size={16}/>
          <span>
            {t('editFieldDrawer:Generate')}
          </span>
        </Button>
      </div>
      <div className='flex flex-col'>
        <span className="opacity-70">
          {selectedField.data?.chat?.appId
            ? (
              <>
                {selectedField.data?.chat?.appId?.slice(0, 15) + '...' }
                <div
                  className="tooltip"
                  data-tip={recentlyCopiedAppId ? 'Copied!' : 'Copy App id'}
                >
                  <button
                    className="btn btn-circle btn-ghost btn-xs ml-1 opacity-70"
                    onClick={handleCopyAppId}
                  >
                    <ClipboardIcon size={12} />
                  </button>
                </div>
              </>
              )
            : 'Not generated yet'
          }
        </span>
      </div>
      <div className='flex items-center'>
        <span className="text-sm py-1">Origins</span>
        <ToolTip
          description={t("editFieldDrawer:Set accepted origin URLs for CORS requests. Leave empty to allow all origins. ';' separate multiple origins.")}
          type="info"
        />
      </div>
      <Input
        value={origins}
        size="sm"
        placeholder='e.g. https://mydomain.com'
        onBlur={() => {
          controller.onUpdateFieldData(
            selectedField.id,
            {
              chat: {
                ...selectedField?.data?.chat,
                origins: origins.trim()
              }
            }
          )
        }}
        onChange={event => onChange('origins', event.target.value)}
      />
    </label>
  )
}
